import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { getAllContactMessages } from "../../redux/thunk";
import { RootState } from "../../redux/store";
import LoadingSpinner from "../../components/elements/LoadingSpinner";
import Pagination from "../../components/Pagination";
import { FaReply } from "react-icons/fa6";
import ContactMessageModal from "../../components/modals/ContactMessageModal";
import { ContactMessage } from "../../types/contactMessages";

const Messages = () => {
  const dispatch = useAppDispatch();
  const { messages, loading, count } = useAppSelector(
    (state: RootState) => state.contactMessages
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedMessage, setSelectedMessage] = useState<ContactMessage | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    dispatch(getAllContactMessages({ page: currentPage }));
  }, [dispatch, currentPage]);

  const handlePagination = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const nextPage = () => {
    if (currentPage < Math.ceil(count / itemsPerPage)) {
      setCurrentPage(currentPage + 1);
    }
  };

  const openReplyModal = (message: ContactMessage) => {
    setSelectedMessage(message);
    setIsModalOpen(true);
  };

  const closeReplyModal = () => {
    setIsModalOpen(false);
    setSelectedMessage(null);
  };

  return (
    <main className="p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-800">Messages From Website</h1>
        <p className="text-sm text-gray-500 mt-1">
          Manage all customer and institutional orders across payment types
        </p>
      </div>

      <section className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">
                  S/N
                </th>
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">
                  E-mail
                </th>
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">
                  Phone Number
                </th>
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">
                  Message
                </th>
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider text-right">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-20 text-center">
                    <LoadingSpinner />
                  </td>
                </tr>
              ) : messages.length > 0 ? (
                messages.map((message, index) => (
                  <tr key={message._id} className="hover:bg-gray-50 border-b border-gray-100 transition-colors">
                    <td className="px-6 py-6 text-sm text-gray-800">
                      {String((currentPage - 1) * itemsPerPage + index + 1).padStart(2, '0')}
                    </td>
                    <td className="px-6 py-6 text-sm font-medium text-gray-800">
                      {message.name}
                    </td>
                    <td className="px-6 py-6 text-sm text-gray-800">
                      {message.email}
                    </td>
                    <td className="px-6 py-6 text-sm text-gray-800">
                      {message.phoneNumber || "---"}
                    </td>
                    <td className="px-6 py-6 text-sm text-gray-800 max-w-xs leading-relaxed">
                      {message.message.length > 80 ? (
                        <>
                          {message.message.substring(0, 80)}...{" "}
                          <button 
                            onClick={() => openReplyModal(message)}
                            className="text-secondary font-semibold hover:underline"
                          >
                            More
                          </button>
                        </>
                      ) : (
                        message.message
                      )}
                    </td>
                    <td className="px-6 py-6 text-right">
                      <button
                        onClick={() => openReplyModal(message)}
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all ${
                          message.isResolved
                            ? "bg-gray-400 cursor-not-allowed opacity-70"
                            : "bg-secondary hover:bg-opacity-95 active:scale-95 shadow-sm"
                        }`}
                        style={{ backgroundColor: message.isResolved ? undefined : '#008060' }}
                      >
                        <FaReply className="w-3.5 h-3.5" />
                        Reply
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="px-6 py-20 text-center text-gray-500 font-medium">
                    No messages found from the website.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="p-8 border-t border-gray-100 flex items-center justify-between">
          <span className="text-sm text-gray-500">
            Showing {(currentPage - 1) * itemsPerPage + 1} - {Math.min(currentPage * itemsPerPage, count)} of {count} messages
          </span>
          <div className="flex gap-4">
            <button
              onClick={prevPage}
              disabled={currentPage === 1}
              className={`px-8 py-2 rounded-xl border border-gray-200 text-sm font-semibold transition-all ${
                currentPage === 1 
                  ? "bg-gray-50 text-gray-300 cursor-not-allowed" 
                  : "bg-white text-gray-700 hover:bg-gray-50 active:scale-95"
              }`}
            >
              Previous
            </button>
            <button
              onClick={nextPage}
              disabled={currentPage >= Math.ceil(count / itemsPerPage)}
              className={`px-8 py-2 rounded-xl border border-gray-200 text-sm font-semibold transition-all ${
                currentPage >= Math.ceil(count / itemsPerPage)
                  ? "bg-gray-50 text-gray-300 cursor-not-allowed" 
                  : "bg-white text-gray-700 hover:bg-gray-50 active:scale-95"
              }`}
            >
              Next
            </button>
          </div>
        </div>
      </section>

      {isModalOpen && selectedMessage && (
        <ContactMessageModal
          message={selectedMessage}
          closeModal={closeReplyModal}
        />
      )}
    </main>
  );
};

export default Messages;
